import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Sparkles, Network, Car, ArrowRight, Server, Globe, RefreshCw, Milestone } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';

interface Route {
    path: string;
    target: string;
    port: number;
    color: string;
}

const DEFAULT_ROUTES: Route[] = [
    { path: '/api', target: 'Backend', port: 3001, color: 'bg-blue-500' },
    { path: '/app', target: 'Frontend', port: 3000, color: 'bg-green-500' },
    { path: '/admin', target: 'Admin Panel', port: 3002, color: 'bg-purple-500' },
];

interface TrafficItem {
    id: number;
    path: string;
    status: 'pending' | 'routed' | 'blocked';
}

export const ReverseProxy = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);
    const [routes, setRoutes] = useState<Route[]>(DEFAULT_ROUTES);
    const [traffic, setTraffic] = useState<TrafficItem[]>([]);
    const [customPath, setCustomPath] = useState('');
    const [routedCount, setRoutedCount] = useState(0);

    const sendTraffic = (path: string) => {
        const id = Date.now();
        const newItem: TrafficItem = { id, path, status: 'pending' };
        setTraffic(prev => [...prev, newItem]);

        // Check if route exists
        setTimeout(() => {
            // Need fresh routes state reference here? Yes.
            // But since setTraffic uses functional update, it's fine for UI.
            // We'll update the routedCount in the effect.
            setTraffic(prev => {
                const currentRoutes = routes; // Closure trap warning? 'routes' is from render scope. 
                // Since this component re-renders, 'sendTraffic' re-defines, capturing fresh 'routes'. 
                // But strict mode might be tricky.

                // Better approach: calculate match inside setTraffic or pass it down.
                // Re-finding route here:
                const matched = prev.find(t => t.id === id);
                if (!matched) return prev;

                // We need to look up in current routes. 
                // 'routes' is available in scope. 
                // Is matchedRoute logic safe?
                // matchedRoute Logic:
                return prev.map(t => {
                    if (t.id !== id) return t;
                    // Find matching route in the *current* routes array (captured from render scope)
                    const routeFound = routes.find(r => t.path.startsWith(r.path));
                    if (routeFound) {
                        setRoutedCount(c => c + 1); // Increment for each successful route
                    }
                    return { ...t, status: routeFound ? 'routed' : 'blocked' };
                });
            });

        }, 1000);

        // Remove after animation
        setTimeout(() => {
            setTraffic(prev => prev.filter(t => t.id !== id));
        }, 3000);
    };

    // Completion Check using Effect (Fixes closure issues)
    useEffect(() => {
        if (routedCount >= 3 && !completed) {
            setCompleted(true);
            completeModule('reverse-proxies', 100);
        }
    }, [routedCount, completed, completeModule]);

    const addRoute = () => {
        if (!customPath.startsWith('/')) return;
        const newRoute: Route = {
            path: customPath,
            target: `Service ${routes.length + 1}`,
            port: 3003 + routes.length,
            color: ['bg-orange-500', 'bg-pink-500', 'bg-cyan-500'][routes.length % 3]
        };
        setRoutes(prev => [...prev, newRoute]);
        setCustomPath('');
    };

    return (
        <div className="min-h-screen bg-slate-900 font-sans text-slate-100 pt-24 pb-20">

            <div className="max-w-6xl mx-auto p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-black font-comic text-white mb-2 flex items-center justify-center gap-3">
                        <Network className="text-cyan-500" /> Reverse Proxy Router
                    </h1>
                    <div className="bg-cyan-900/50 text-cyan-400 px-4 py-1 rounded-full text-sm font-bold border border-cyan-800 inline-block mb-3">
                        🚦 The Traffic Cop
                    </div>
                    <p className="text-slate-400 max-w-2xl mx-auto mb-6">
                        A reverse proxy (like Nginx) routes incoming traffic to different backend services based on URL paths.
                    </p>

                    {/* Mission Box */}
                    <div className="max-w-xl mx-auto bg-slate-800 p-4 rounded-xl border-l-4 border-cyan-500 text-left shadow-lg">
                        <h3 className="font-bold text-white flex items-center gap-2 mb-2">
                            <Milestone size={18} className="text-cyan-400" /> Routing Challenge
                        </h3>
                        <p className="text-slate-300 text-sm">
                            Click the buttons on the left to send traffic.
                            <span className="text-cyan-400 font-bold"> Successfully route 3 requests</span> to complete the module!
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Traffic Generator */}
                    <div className="bg-slate-800 rounded-3xl p-6 border border-slate-700">
                        <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                            <Globe className="text-slate-500" /> Incoming Requests
                        </h2>

                        <div className="space-y-3">
                            {routes.map(route => (
                                <button
                                    key={route.path}
                                    onClick={() => sendTraffic(route.path)}
                                    className={`w-full ${route.color} hover:opacity-90 text-white font-bold px-4 py-3 rounded-xl transition-all flex items-center justify-between group`}
                                >
                                    <span className="font-mono">{route.path}</span>
                                    <Car size={20} className="group-hover:translate-x-1 transition-transform" />
                                </button>
                            ))}
                            <button
                                onClick={() => sendTraffic('/unknown')}
                                className="w-full bg-red-600 hover:bg-red-500 text-white font-bold px-4 py-3 rounded-xl transition-all flex items-center justify-between"
                            >
                                <span className="font-mono">/unknown</span>
                                <span className="text-xs opacity-70">404</span>
                            </button>
                        </div>

                        {/* Add Custom Route */}
                        <div className="mt-6 pt-6 border-t border-slate-700">
                            <label className="text-sm font-bold text-slate-400 mb-2 block">Add Custom Route</label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={customPath}
                                    onChange={e => setCustomPath(e.target.value)}
                                    placeholder="/custom-path"
                                    className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm font-mono focus:border-cyan-500 outline-none"
                                />
                                <button
                                    onClick={addRoute}
                                    disabled={!customPath.startsWith('/')}
                                    className="px-4 bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 text-white rounded-lg font-bold transition-colors"
                                >
                                    +
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Nginx Visualization */}
                    <div className="bg-slate-800 rounded-3xl p-6 border border-slate-700 relative overflow-hidden">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-bold flex items-center gap-2">
                                <RefreshCw className="text-cyan-500" /> Nginx Proxy
                            </h2>
                            <span className="text-xs font-mono bg-slate-900 px-2 py-1 rounded text-green-400">Routed: {routedCount}</span>
                        </div>

                        {/* Proxy Box */}
                        <div className="bg-slate-900 rounded-2xl p-6 border-2 border-cyan-500 relative min-h-[300px]">
                            <div className="text-center mb-4">
                                <div className="inline-flex items-center gap-2 bg-cyan-500 text-white px-4 py-2 rounded-xl font-bold">
                                    <Server size={20} /> nginx:80
                                </div>
                            </div>

                            {/* Traffic Animation */}
                            <AnimatePresence>
                                {traffic.map(t => {
                                    const matchedRoute = routes.find(r => t.path.startsWith(r.path));
                                    return (
                                        <motion.div
                                            key={t.id}
                                            initial={{ x: -100, opacity: 0 }}
                                            animate={{
                                                x: t.status === 'pending' ? 0 : 100,
                                                opacity: t.status === 'blocked' ? 0 : 1,
                                                scale: t.status === 'blocked' ? 0 : 1
                                            }}
                                            exit={{ opacity: 0, x: 200 }}
                                            className={`absolute left-1/2 -translate-x-1/2 ${matchedRoute?.color || 'bg-red-500'} text-white px-3 py-1 rounded-lg text-sm font-mono flex items-center gap-2`}
                                            style={{ top: `${80 + (traffic.indexOf(t) * 40)}px` }}
                                        >
                                            <Car size={14} />
                                            {t.path}
                                            {t.status === 'routed' && <ArrowRight size={14} />}
                                        </motion.div>
                                    );
                                })}
                            </AnimatePresence>

                            {traffic.length === 0 && (
                                <div className="text-center text-slate-600 py-12">
                                    <Car size={32} className="mx-auto mb-2 opacity-50" />
                                    <p className="text-sm">No traffic yet</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Backend Services */}
                    <div className="bg-slate-800 rounded-3xl p-6 border border-slate-700">
                        <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                            <Server className="text-slate-500" /> Backend Services
                        </h2>

                        <div className="space-y-3">
                            {routes.map(route => (
                                <motion.div
                                    key={route.path}
                                    animate={{
                                        boxShadow: traffic.some(t => t.path.startsWith(route.path) && t.status === 'routed')
                                            ? '0 0 20px rgba(34, 197, 94, 0.5)'
                                            : 'none'
                                    }}
                                    className={`bg-slate-900 rounded-xl p-4 border-2 border-slate-700 flex items-center gap-4`}
                                >
                                    <div className={`w-10 h-10 ${route.color} rounded-lg flex items-center justify-center text-white`}>
                                        <Server size={20} />
                                    </div>
                                    <div className="flex-1">
                                        <div className="font-bold text-white">{route.target}</div>
                                        <div className="text-xs text-slate-500 font-mono">localhost:{route.port}</div>
                                    </div>
                                    <div className="text-sm font-mono text-slate-400">{route.path}</div>
                                </motion.div>
                            ))}
                        </div>

                        {/* Nginx Config */}
                        <div className="mt-6 bg-slate-900 rounded-xl p-4 font-mono text-xs text-green-400">
                            <div className="text-slate-500 mb-2"># nginx.conf</div>
                            {routes.map(r => (
                                <div key={r.path}>
                                    location {r.path} {'{'}<br />
                                    <span className="pl-4">proxy_pass http://localhost:{r.port};</span><br />
                                    {'}'}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {completed && (
                <ModuleSuccess
                    title="Traffic Controller"
                    nextModulePath="/"
                    onRetry={() => {
                        setCompleted(false);
                        setRoutedCount(0);
                    }}
                >
                    <div className="bg-slate-700 p-4 rounded-xl text-left border border-slate-600">
                        <h4 className="font-bold text-cyan-400 mb-2">🚦 Traffic Sorted!</h4>
                        <p className="text-slate-300 text-sm">
                            You've set up a Reverse Proxy. It acts as the "receptionist" for your backend, sending everything to the right place.
                        </p>
                    </div>
                </ModuleSuccess>
            )}

        </div>
    );
};
